import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import JobShipments from '../components/JobShipments';

const STAGES = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'waiting_on_art', label: 'Waiting on Art' },
  { key: 'waiting_on_blanks', label: 'Waiting on Blanks' },
  { key: 'pre_production', label: 'Pre-Production' },
  { key: 'production', label: 'In Production' },
  { key: 'ready_to_ship', label: 'Ready to Ship' },
  { key: 'completed', label: 'Completed' },
];
const LABEL = Object.fromEntries(STAGES.map((s) => [s.key, s.label]));

export default function ProductionJobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user && (user.role === 'admin' || user.role === 'manager');
  const [job, setJob] = useState(null);
  const [msg, setMsg] = useState('');
  const [shipBy, setShipBy] = useState('');
  const [titleDraft, setTitleDraft] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [blanks, setBlanks] = useState([]);
  const [bForm, setBForm] = useState({ carrier: 'fedex', tracking_number: '', label: '' });
  const [bMsg, setBMsg] = useState('');
  const [bBusy, setBBusy] = useState(false);

  /* @@PORTAL_JOB_NOTE_v57@@ */
  const [noteDraft, setNoteDraft] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  async function addNote() {
    const text = noteDraft.trim();
    if (!text) return;
    setNoteBusy(true);
    try {
      const res = await apiFetch(`/api/production/${id}/note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: text }),
      });
      if (res.ok) { setNoteDraft(''); load(); }
    } finally { setNoteBusy(false); }
  }
  /* @@PROD_DELETE_BTN_v58@@ */
  async function removeJob() {
    if (!window.confirm('Delete this production job? Its history, shipments, and blank-tracking are removed. This cannot be undone. (The linked PO may drop from the cache on the next Zoho refresh.)')) return;
    const res = await apiFetch(`/api/production/${id}`, { method: 'DELETE' });
    if (res.ok) navigate('/production');
  }
  async function load() {
    const res = await apiFetch(`/api/production/${id}`);
    if (res.ok) { const d = await res.json(); setJob(d.job); setShipBy(d.job.ship_by_date ? String(d.job.ship_by_date).slice(0, 10) : ''); setTitleDraft(d.job.job_title || ''); }
    else if (res.status === 404) setMsg('Job not found.');
    apiFetch(`/api/blanks/by-job/${id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((bd) => { if (bd) setBlanks(bd.shipments || []); })
      .catch(() => {});
  }
  useEffect(() => { load(); }, [id]);

  async function patch(body) {
    const res = await apiFetch(`/api/production/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) { const d = await res.json(); setJob((j) => ({ ...j, ...d.job })); load(); }
    else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Update failed.'); }
  }

  async function saveTitle() {
    await patch({ job_title: titleDraft });
    setEditingTitle(false);
  }
  async function revertTitle() {
    if (!window.confirm('Revert to the title from Zoho? Your custom title will be replaced.')) return;
    await patch({ job_title: '' });
    setEditingTitle(false);
  }

  async function addBlank() {
    setBMsg('');
    const res = await apiFetch('/api/blanks', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ job_id: id, ...bForm }),
    });
    if (res.ok) { setBForm({ carrier: 'fedex', tracking_number: '', label: '' }); load(); }
    else { const d = await res.json().catch(() => ({})); setBMsg(d.error || 'Could not add.'); }
  }
  async function removeBlank(sid) {
    if (!window.confirm('Remove this shipment?')) return;
    const res = await apiFetch(`/api/blanks/${sid}`, { method: 'DELETE' });
    if (res.ok) load();
  }
  async function refreshBlanks() {
    setBMsg(''); setBBusy(true);
    try {
      const res = await apiFetch(`/api/blanks/refresh/${id}`, { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { setBMsg(`Checked ${d.total} shipment(s): ${d.changed} updated, ${d.skipped} no data.`); load(); }
      else setBMsg(d.error || 'Refresh failed.');
    } catch (e) { setBMsg('Could not reach the server.'); }
    finally { setBBusy(false); }
  }

  function fmt(s) { if (!s) return '—'; try { return new Date(s).toLocaleString(); } catch { return '—'; } }
  function fmtDate(s) { if (!s) return '—'; try { return new Date(s).toLocaleDateString(); } catch { return '—'; } }
  function fmtMoney(n, cur) { if (n == null || n === '') return '—'; try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur || 'USD' }).format(n); } catch { return String(n); } }

  // expected next stage (mostly-ordered default)
  const curIdx = job ? STAGES.findIndex((s) => s.key === job.status) : -1;
  const next = curIdx >= 0 && curIdx < STAGES.length - 1 ? STAGES[curIdx + 1] : null;

  if (msg && !job) return <div className="hq-page-content"><div className="hq-page-head"><h1>Production</h1><p>{msg}</p></div></div>;
  if (!job) return <div className="hq-page-content"><div className="hq-page-head"><p>Loading…</p></div></div>;

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>PO {job.po_number || ''}</h1>
        <p>{job.vendor_name || 'Unknown vendor'} · <span className={`hq-pill hq-stage-${job.status}`}>{LABEL[job.status]}</span></p>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head"><h2>Status</h2></div>
        <div style={{ padding: 20 }}>
          {canManage ? (
            <div className="hq-form" style={{ alignItems: 'center' }}>
              {next && (
                <button className="hq-btn" onClick={() => patch({ status: next.key })}>
                  Advance → {next.label}
                </button>
              )}
              <select className="hq-select" value={job.status} onChange={(e) => patch({ status: e.target.value })}>
                {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <input className="hq-input" type="date" value={shipBy}
                onChange={(e) => setShipBy(e.target.value)} />
              <button className="hq-btn hq-btn-ghost" onClick={() => patch({ ship_by_date: shipBy })}>Save ship-by</button>
            </div>
          ) : (
            <p style={{ margin: 0, color: 'var(--hq-muted)' }}>Current stage: {LABEL[job.status]}</p>
          )}
          {msg && <p className="hq-msg">{msg}</p>}
        </div>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>What's being produced</h2>
          {(job.line_items || []).length > 0 && (
            <span className="hq-role">
              {job.line_items.length} item{job.line_items.length === 1 ? '' : 's'}
              {(() => { const tot = job.line_items.reduce((a, li) => a + (Number(li.quantity) || 0), 0); return tot ? ` · ${tot} units` : ''; })()}
            </span>
          )}
        </div>
        <div style={{ overflowX: 'auto' }}>
        <table className="hq-table">
          <thead><tr><th>SKU</th><th>Item</th><th>Description</th><th>Qty</th><th>Invoiced</th><th>Unit</th><th>Rate</th><th>Tax</th><th>Total</th></tr></thead>
          <tbody>
            {(job.line_items || []).map((li, idx) => (
              <tr key={idx}>
                <td>{li.sku || '—'}</td>
                <td>{li.name || '—'}</td>
                <td style={{ color: 'var(--hq-muted)' }}>{li.description || '—'}</td>
                <td>{li.quantity != null ? li.quantity : '—'}</td>
                <td>{li.quantity_invoiced != null ? li.quantity_invoiced : '—'}</td>
                <td>{li.unit || '—'}</td>
                <td>{fmtMoney(li.rate, '')}</td>
                <td>{li.tax_name ? `${li.tax_name}${li.tax_percentage != null ? ` (${li.tax_percentage}%)` : ''}` : '—'}</td>
                <td>{fmtMoney(li.item_total, '')}</td>
              </tr>
            ))}
            {(!job.line_items || job.line_items.length === 0) && (
              <tr><td className="hq-empty" colSpan={9}>No line items cached. Refresh the PO from Zoho on the PO list.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head"><h2>Details</h2></div>
        <table className="hq-table">
          <tbody>
            <tr><td className="hq-role">Project title</td><td>
              {canManage && editingTitle ? (
                <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input className="hq-input" value={titleDraft} placeholder="Project title"
                    style={{ minWidth: 240 }} onChange={(e) => setTitleDraft(e.target.value)} />
                  <button className="hq-btn" onClick={saveTitle}>Save</button>
                  <button className="hq-btn hq-btn-ghost" onClick={() => { setTitleDraft(job.job_title || ''); setEditingTitle(false); }}>Cancel</button>
                </span>
              ) : (
                <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>{job.job_title || '—'}</span>
                  {job.job_title_overridden && <span className="hq-role" title="Manually edited; will not be overwritten by a Zoho refresh">(edited)</span>}
                  {canManage && <button className="hq-linkish" onClick={() => { setTitleDraft(job.job_title || ''); setEditingTitle(true); }}>Edit</button>}
                  {canManage && job.job_title_overridden && <button className="hq-linkish" onClick={revertTitle}>Revert to Zoho</button>}
                </span>
              )}
            </td></tr>
            <tr><td className="hq-role">PO number</td><td>{job.po_number || '—'}</td></tr>
            <tr><td className="hq-role">Vendor</td><td>
              {job.vendor_local_id
                ? <button className="hq-linkish" onClick={() => navigate(`/vendors/${job.vendor_local_id}`)}>{job.vendor_name || '—'}</button>
                : (job.vendor_name || '—')}
            </td></tr>
            <tr><td className="hq-role">Ship by</td><td>{fmtDate(job.ship_by_date)}</td></tr>
            <tr><td className="hq-role">Completed</td><td>{fmt(job.completed_at)}</td></tr>
          </tbody>
        </table>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head"><h2>Add a note</h2></div>
        <div style={{ padding: 20 }}>
          <div className="hq-form">
            <input className="hq-input" placeholder="Note for this job…" value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)} style={{ minWidth: 280, flex: 1 }} />
            <button className="hq-btn" onClick={addNote} disabled={noteBusy}>{noteBusy ? 'Saving…' : 'Add note'}</button>
          </div>
        </div>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head"><h2>History</h2></div>
        <table className="hq-table">
          <thead><tr><th>When</th><th>Change</th><th>By</th></tr></thead>
          <tbody>
            {(job.history || []).map((h, idx) => (
              <tr key={idx}>
                <td>{fmt(h.created_at)}</td>
                <td>{h.from_status ? `${LABEL[h.from_status] || h.from_status} → ` : ''}{LABEL[h.to_status] || h.to_status}{h.note ? ` (${h.note})` : ''}</td>
                <td style={{ color: 'var(--hq-muted)' }}>{h.by || '—'}</td>
              </tr>
            ))}
            {(!job.history || job.history.length === 0) && (
              <tr><td className="hq-empty" colSpan={3}>No history yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Inbound blanks tracking</h2>
          {canManage && (
            <button className="hq-btn hq-btn-ghost" onClick={refreshBlanks} disabled={bBusy}>
              {bBusy ? 'Checking…' : 'Refresh tracking'}
            </button>
          )}
        </div>
        <table className="hq-table">
          <thead><tr><th>Label</th><th>Carrier</th><th>Tracking #</th><th>Status</th><th>ETA</th>{canManage && <th></th>}</tr></thead>
          <tbody>
            {blanks.map((sh) => (
              <tr key={sh.id}>
                <td>{sh.label || '—'}</td>
                <td style={{ textTransform: 'uppercase' }}>{sh.carrier}</td>
                <td>{sh.tracking_number}</td>
                <td>{sh.status || '—'}</td>
                <td>{sh.estimated_delivery ? fmtDate(sh.estimated_delivery) : '—'}</td>
                {canManage && <td><button className="hq-linkish" onClick={() => removeBlank(sh.id)}>Remove</button></td>}
              </tr>
            ))}
            {blanks.length === 0 && (
              <tr><td className="hq-empty" colSpan={canManage ? 6 : 5}>No inbound shipments yet.</td></tr>
            )}
          </tbody>
        </table>
        {canManage && (
          <div style={{ padding: 16 }}>
            <div className="hq-form">
              <select className="hq-select" value={bForm.carrier} onChange={(e) => setBForm({ ...bForm, carrier: e.target.value })}>
                <option value="fedex">FedEx</option>
                <option value="ups">UPS</option>
                <option value="usps">USPS</option>
                <option value="other">Other</option>
              </select>
              <input className="hq-input" placeholder="Tracking number" value={bForm.tracking_number}
                onChange={(e) => setBForm({ ...bForm, tracking_number: e.target.value })} />
              <input className="hq-input" placeholder="Label (e.g. Gildan 5000 200ct)" value={bForm.label}
                onChange={(e) => setBForm({ ...bForm, label: e.target.value })} />
              <button className="hq-btn" onClick={addBlank}>Add shipment</button>
            </div>
            {bMsg && <p className="hq-msg">{bMsg}</p>}
          </div>
        )}
        {!canManage && bMsg && <p className="hq-msg" style={{ margin: '0 20px 16px' }}>{bMsg}</p>}
      </div>

      {/* @@JOB_SHIPMENTS_PLACEMENT_v1@@ */}
      <JobShipments jobId={id} canManage={canManage} />

      <button className="hq-signout" style={{ width: 'auto', padding: '0 16px' }} onClick={() => navigate('/production')}>Back to calendar</button>
      <button className="hq-signout" style={{ width: 'auto', padding: '0 16px', borderColor: 'rgba(255,90,82,.3)', color: '#ff5a52' }} onClick={removeJob}>Delete job</button>
    </div>
  );
}
